import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type BannerInput,
  type BannerRow,
  type CategoryArtRow,
  type ShopFrontDestinationGroup,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * The shop front: the banner carousels, and the photograph on a category tile.
 *
 * ── WHY ONE SCREEN FOR BOTH ─────────────────────────────────────────────────
 *
 * Because they are one decision. Somebody arranging a malaria campaign sets a
 * banner for it and puts a photograph on the malaria tile in the same sitting,
 * and splitting that across two rooms means doing half of it and forgetting the
 * rest.
 *
 * ── WHAT THE ROW COLUMN IS FOR ──────────────────────────────────────────────
 *
 * Two independent carousels. A single strip of eight slides shows one thing at
 * a time; two strips of four say twice as much per turn and give the shop front
 * a composed look rather than one blinking box.
 *
 * ── WHY EVERY SLIDE DECLARES A SHELF ────────────────────────────────────────
 *
 * Human and veterinary are walled shops. A banner for a children's syrup above
 * the cattle shelf is the wall leaking in the most visible place in the app, so
 * the sector is required and `Both shelves` has to be chosen deliberately.
 */
export function ShopFrontScreen() {
  const [tab, setTab] = useState<'banners' | 'tiles'>('banners');
  const [editing, setEditing] = useState<BannerRow | 'new' | null>(null);

  if (editing) {
    return <BannerForm banner={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />;
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Shop front</h1>
      <p className="m-0 mt-1 max-w-[64ch] text-[13px] leading-relaxed text-muted">
        What a buyer sees under the search box. How fast the carousels turn and how many category
        tiles show are in{' '}
        <b className="font-semibold text-ink">Settings &rsaquo; Shop front</b> — this is the content
        itself.
      </p>

      <div className="mb-5 mt-3 flex gap-1 border-b border-line">
        {(
          [
            ['banners', 'Banners'],
            ['tiles', 'Categories'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`shopfront-tab-${key}`}
            className={cn(
              'min-h-11 border-b-2 bg-transparent px-3 text-[13.5px] font-semibold',
              tab === key ? 'border-blue text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'banners' ? <Banners onEdit={setEditing} /> : <CategoryPictures />}
    </div>
  );
}

// ── Banners ────────────────────────────────────────────────────────────────

function Banners({ onEdit }: { onEdit: (banner: BannerRow | 'new') => void }) {
  const queryClient = useQueryClient();
  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'banners'],
    queryFn: ({ signal }) => api.adminBanners(undefined, signal),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteBanner(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] }),
  });

  const reorder = useMutation({
    mutationFn: ({ row, ids }: { row: number; ids: number[] }) => api.reorderBanners(row, ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] }),
  });

  if (isLoading) {
    return (
      <div className="space-y-2.5">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[88px] w-full" radius="card" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <AdminError title="We couldn't load the slides" error={error} onRetry={() => refetch()} />;
  }

  const banners = data ?? [];
  const rows = [1, 2, 3].filter((row) => banners.some((banner) => banner.row === row));

  return (
    <div>
      <CarouselTiming />

      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="m-0 text-[12px] text-muted">
          {banners.filter((banner) => banner.is_live).length} of {banners.length} showing now
        </p>
        <Button onClick={() => onEdit('new')} data-testid="banner-new">
          Add a slide
        </Button>
      </div>

      {banners.length === 0 ? (
        <EmptyState
          title="No slides yet"
          body="A slide is an image, a headline and somewhere to go. Buyers see nothing here until there is at least one."
          action={<Button onClick={() => onEdit('new')}>Add the first one</Button>}
        />
      ) : null}

      {rows.map((row) => {
        const inRow = banners
          .filter((banner) => banner.row === row)
          .sort((a, b) => a.position - b.position);

        return (
          <section key={row} className="mb-6" data-testid={`banner-group-${row}`}>
            <h2 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              Row {row} — {inRow.length} {inRow.length === 1 ? 'slide' : 'slides'}
            </h2>

            <div className="space-y-2">
              {inRow.map((banner, index) => (
                <SlideRow
                  key={banner.id}
                  banner={banner}
                  canMoveUp={index > 0}
                  canMoveDown={index < inRow.length - 1}
                  busy={reorder.isPending || remove.isPending}
                  onEdit={() => onEdit(banner)}
                  onDelete={() => {
                    if (
                      window.confirm(
                        `Delete “${banner.headline}”? The artwork stays in the media library.`,
                      )
                    ) {
                      remove.mutate(banner.id);
                    }
                  }}
                  onMove={(direction) => {
                    // Rebuilt by filtering rather than swapping in place, so
                    // there is no index TypeScript has to be told is in range
                    // and no way to produce a list with a hole in it.
                    const ids = inRow.map((item) => item.id);
                    const to = index + direction;
                    const moved = ids[index]!;
                    ids.splice(index, 1);
                    ids.splice(to, 0, moved);
                    reorder.mutate({ row, ids });
                  }}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * How fast the carousel turns, where the slides are.
 *
 * ────────────────── WHY IT IS HERE AND ALSO IN SETTINGS ──────────────────
 *
 * It was only in Settings — Shop front, two screens away from the slides it
 * governs. Somebody arranging banners who wants them to hold a little longer
 * has to leave, find a settings group, and come back, and the likeliest outcome
 * is that they never find it at all and conclude the speed is fixed.
 *
 * Same setting, same audit trail, written through the same endpoint. This is a
 * second door onto one value, not a second value.
 */
function CarouselTiming() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: ({ signal }) => api.adminSettings(signal),
  });

  const values = (data?.values ?? {}) as Record<string, unknown>;

  const seconds = Number(values['shopfront.banner_interval_seconds'] ?? 6);
  const rows = Number(values['shopfront.banner_rows'] ?? 2);
  const autoplay = values['shopfront.banner_autoplay'] !== false;
  const ratio = String(values['shopfront.banner_ratio'] ?? '5/1');

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateAdminSettings(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  return (
    <div className="mb-4 rounded-card border border-line bg-paper p-3.5" data-testid="carousel-timing">
      <p className="m-0 text-[12px] font-semibold text-ink">How the carousel behaves</p>

      <div className="mt-2.5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-[5px] block text-[11px] font-bold text-muted">
            Seconds per slide
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Slower"
              disabled={save.isPending || seconds <= 3}
              onClick={() =>
                save.mutate({ 'shopfront.banner_interval_seconds': Math.max(3, seconds - 1) })
              }
              className="grid h-10 w-10 shrink-0 place-items-center rounded-btn border border-line bg-white disabled:opacity-40"
            >
              <Icon name="minus" size={14} strokeWidth={2.4} />
            </button>

            <span
              className="min-w-[46px] text-center font-mono text-[15px] font-bold text-ink"
              data-testid="carousel-seconds"
            >
              {seconds}s
            </span>

            <button
              type="button"
              aria-label="Longer"
              disabled={save.isPending || seconds >= 30}
              onClick={() =>
                save.mutate({ 'shopfront.banner_interval_seconds': Math.min(30, seconds + 1) })
              }
              className="grid h-10 w-10 shrink-0 place-items-center rounded-btn border border-line bg-white disabled:opacity-40"
            >
              <Icon name="plus" size={14} strokeWidth={2.4} />
            </button>
          </div>
          <span className="mt-1 block text-[10.5px] leading-snug text-faint">
            Below about five seconds a slide turns while somebody is still reading it.
          </span>
        </label>

        <label className="block">
          <span className="mb-[5px] block text-[11px] font-bold text-muted">Rows</span>
          <select
            value={rows}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ 'shopfront.banner_rows': Number(event.target.value) })
            }
            className="min-h-10 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink"
          >
            <option value={0}>None — hide the banners</option>
            <option value={1}>One row</option>
            <option value={2}>Two rows</option>
            <option value={3}>Three rows</option>
          </select>
          <span className="mt-1 block text-[10.5px] leading-snug text-faint">
            None hides them without deleting a single slide.
          </span>
        </label>

        <label className="block">
          <span className="mb-[5px] block text-[11px] font-bold text-muted">Shape of the strip</span>
          <select
            value={ratio}
            disabled={save.isPending}
            onChange={(event) => save.mutate({ 'shopfront.banner_ratio': event.target.value })}
            data-testid="banner-ratio"
            className="min-h-10 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink"
          >
            <option value="5/1">Very wide, 5:1 × 1600 × 320</option>
            <option value="4/1">Wide, 4:1 × 1600 × 400</option>
            <option value="3/1">Medium, 3:1 × 1500 × 500</option>
            <option value="16/7">Tall, 16:7 × 1600 × 700</option>
          </select>
          {/* The single most useful control when a design is being cut off,
              and the one nobody thinks to look for. */}
          <span className="mt-1 block text-[10.5px] leading-snug text-faint">
            Match this to your artwork and nothing is cropped. Phones stay 16:7.
          </span>
        </label>

        <label className="flex items-start gap-2 pt-5">
          <input
            type="checkbox"
            checked={autoplay}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ 'shopfront.banner_autoplay': event.target.checked })
            }
            className="mt-0.5 h-4 w-4"
          />
          <span className="text-[12px] leading-snug text-ink">
            Turn by itself
            <span className="mt-0.5 block text-[10.5px] text-faint">
              Always pauses while somebody is reading a slide.
            </span>
          </span>
        </label>
      </div>

      {/* Not configurable, and said so. For somebody with a vestibular
          disorder a moving shop front is a page they cannot use, so it is the
          viewer's decision rather than the merchandiser's. */}
      <p className="m-0 mt-2.5 text-[10.5px] leading-snug text-faint">
        Anybody whose device asks for reduced motion gets a still carousel they page through
        themselves, whatever is set here.
      </p>

      {save.error ? (
        <p className="m-0 mt-1.5 text-[11px] font-medium text-danger">
          {save.error instanceof ApiError ? save.error.message : 'That did not save.'}
        </p>
      ) : null}
    </div>
  );
}

function SlideRow({
  banner,
  canMoveUp,
  canMoveDown,
  busy,
  onEdit,
  onDelete,
  onMove,
}: {
  banner: BannerRow;
  canMoveUp: boolean;
  canMoveDown: boolean;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (direction: 1 | -1) => void;
}) {
  return (
    <div
      data-testid={`banner-${banner.id}`}
      // flex-wrap, not a narrower thumbnail: the controls are 36px targets
      // (§11) and four of them beside a preview cannot fit 320px however
      // they are squeezed. They drop to their own line instead, which is
      // the one arrangement that keeps both the target size and the width.
      className="flex flex-wrap items-center gap-3 rounded-card border border-line bg-white p-2.5"
    >
      {/* A thumbnail at the real aspect ratio, so what is arranged here looks
          like what buyers get. A square preview of a 16:7 banner shows a crop
          nobody will ever see. */}
      <div
        // 5:1, matching the wide strip, so the list reads like the shop front.
        className="relative h-[18px] w-[90px] shrink-0 overflow-hidden rounded sm:h-[24px] sm:w-[120px]"
        style={{ background: banner.tint }}
      >
        {banner.image ? (
          <img src={banner.image} alt="" aria-hidden className="h-full w-full object-cover" />
        ) : null}
        <span className="absolute inset-0 grid place-items-center px-1 text-center text-[8px] font-bold leading-tight text-white/90">
          {banner.image ? '' : 'No image'}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="m-0 truncate text-[13px] font-semibold text-ink">{banner.headline}</p>
          <ShelfChip sector={banner.sector} />
        </div>

        {banner.dormant_because ? (
          <p className="m-0 mt-0.5 text-[11px] font-medium text-caution">{banner.dormant_because}</p>
        ) : (
          <p className="m-0 mt-0.5 flex items-center gap-1 text-[11px] text-verified">
            <Icon name="check" size={11} strokeWidth={2.6} />
            Showing now
          </p>
        )}

        <p className="m-0 mt-0.5 text-[10.5px] text-faint">
          {banner.impressions.toLocaleString()} seen · {banner.clicks.toLocaleString()} pressed
          {banner.link_path ? ` · ${banner.link_path}` : ' · no link'}
        </p>
      </div>

      <div className="flex w-full shrink-0 items-center justify-end gap-1 sm:w-auto">
        <IconBtn
          icon="up"
          label={`Move ${banner.headline} up`}
          disabled={!canMoveUp || busy}
          onClick={() => onMove(-1)}
        />
        <IconBtn
          icon="down"
          label={`Move ${banner.headline} down`}
          disabled={!canMoveDown || busy}
          onClick={() => onMove(1)}
        />
        <IconBtn icon="settings" label={`Edit ${banner.headline}`} onClick={onEdit} />
        <IconBtn
          icon="trash"
          label={`Delete ${banner.headline}`}
          disabled={busy}
          onClick={onDelete}
          danger
        />
      </div>
    </div>
  );
}

function ShelfChip({ sector }: { sector: BannerRow['sector'] }) {
  // `bg-vet-tint text-vet` until now — classes that resolve to NOTHING. The
  // violet veterinary palette was removed when the two shops became one brand,
  // and `vet.spec.ts` asserts those tokens stay gone; this chip kept naming
  // them, so the animal-health slide has been rendering unstyled ever since.
  const look =
    sector === 'veterinary'
      ? 'bg-ink/[0.07] text-ink'
      : sector === 'human'
        ? 'bg-blue-tint text-blue'
        : 'bg-paper text-muted';

  const label = sector === 'veterinary' ? 'Animal' : sector === 'human' ? 'Human' : 'Both';

  return (
    <span
      className={cn(
        'shrink-0 rounded-pill px-1.5 py-px font-mono text-[9px] uppercase tracking-[0.08em]',
        look,
      )}
    >
      {label}
    </span>
  );
}

function IconBtn({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: 'up' | 'down' | 'settings' | 'trash';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'grid h-9 w-9 place-items-center rounded-btn border border-line bg-white disabled:opacity-35',
        danger ? 'text-danger hover:border-danger' : 'text-muted hover:border-blue hover:text-blue',
      )}
    >
      <Icon name={icon} size={14} strokeWidth={2} />
    </button>
  );
}

// ── The form ───────────────────────────────────────────────────────────────

const TINTS = [
  ['#0A5239', 'ChiCare green'],
  ['#1B4D8F', 'Deep blue'],
  ['#5B3E8F', 'Violet'],
  ['#9C2B21', 'Danger red'],
  ['#8F6A1F', 'Amber'],
  ['#1F6F4A', 'Animal green'],
  ['#131A17', 'Near black'],
] as const;

/** The canvas to draw on, for each strip shape the console offers. */
const WIDE_CANVAS: Record<string, string> = {
  '5/1': `1600 ${'\u00d7'} 320`,
  '4/1': `1600 ${'\u00d7'} 400`,
  '3/1': `1500 ${'\u00d7'} 500`,
  '16/7': `1600 ${'\u00d7'} 700`,
};

function BannerForm({ banner, onDone }: { banner: BannerRow | null; onDone: () => void }) {
  const queryClient = useQueryClient();

  // The configured strip shape, so the form asks for artwork of the right size
  // and previews it at the shape it will actually be shown at.
  const { data: settings } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: ({ signal }) => api.adminSettings(signal),
  });

  const stripRatio = String(
    ((settings?.values ?? {}) as Record<string, unknown>)['shopfront.banner_ratio'] ?? '5/1',
  );
  const [form, setForm] = useState<BannerInput>({
    headline: banner?.headline ?? '',
    subline: banner?.subline ?? '',
    cta_label: banner?.cta_label ?? '',
    link_path: banner?.link_path ?? '',
    image_media_id: banner?.image_media_id ?? null,
    mobile_image_media_id: banner?.mobile_image_media_id ?? null,
    tint: banner?.tint ?? '#0A5239',
    overlay: banner?.overlay ?? 'none',
    fit: banner?.fit ?? 'cover',
    sector: banner?.sector ?? 'both',
    row: banner?.row ?? 1,
    is_active: banner?.is_active ?? true,
    starts_at: banner?.starts_at?.slice(0, 10) ?? '',
    ends_at: banner?.ends_at?.slice(0, 10) ?? '',
  });

  const [preview, setPreview] = useState<string | null>(banner?.image ?? null);
  const [mobilePreview, setMobilePreview] = useState<string | null>(banner?.image_mobile ?? null);

  // Which shape the preview is showing. The whole point of two canvases is
  // that an admin can SEE both before publishing, rather than discovering
  // the phone crop from a screenshot a week later.
  const [previewAs, setPreviewAs] = useState<'wide' | 'phone'>('wide');

  const set = <K extends keyof BannerInput>(key: K, value: BannerInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const upload = useMutation({
    mutationFn: async ({ file, slot }: { file: File; slot: 'wide' | 'phone' }) => ({
      asset: await api.uploadMedia(file, 'banners', form.headline || undefined),
      slot,
    }),
    onSuccess: ({ asset, slot }) => {
      if (slot === 'wide') {
        set('image_media_id', asset.id);
        setPreview(asset.url);
      } else {
        set('mobile_image_media_id', asset.id);
        setMobilePreview(asset.url);
        setPreviewAs('phone');
      }
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: BannerInput = {
        ...form,
        // Empty boxes mean "nothing", not "an empty string". The API would
        // accept "" for a link and the buyer would get a slide that navigates
        // to the current page when pressed.
        subline: form.subline || null,
        cta_label: form.cta_label || null,
        link_path: form.link_path || null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        mobile_image_media_id: form.mobile_image_media_id ?? null,
        overlay: form.overlay,
        fit: form.fit,
      };

      return banner ? api.updateBanner(banner.id, payload) : api.createBanner(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      onDone();
    },
  });

  const errors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 flex min-h-9 items-center gap-1.5 text-[12.5px] font-semibold text-muted hover:text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.2} />
        Shop front
      </button>

      <h1 className="m-0 font-display text-[23px] font-bold tracking-[-0.02em]">
        {banner ? 'Edit this slide' : 'A new slide'}
      </h1>

      {/* The preview is the point of the screen. Somebody choosing a tint and a
          headline needs to see the thing a buyer sees, at the shape a buyer
          sees it, before they publish it to the front page. */}
      {/* There are two shapes, so there are two previews. Somebody choosing
          a tint and a headline needs to see what a buyer sees, at the shape
          a buyer sees it, before it reaches the front page. */}
      <div className="mt-4 flex gap-1" role="group" aria-label="Preview size">
        {(
          [
            ['wide', 'Wide — 640px and up'],
            ['phone', 'Phone — under 640px'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setPreviewAs(key)}
            aria-pressed={previewAs === key}
            data-testid={`preview-as-${key}`}
            className={cn(
              'min-h-9 rounded-btn border px-2.5 text-[11.5px] font-semibold',
              previewAs === key
                ? 'border-blue bg-blue-tint text-blue'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        // The phone preview is held at a phone's width rather than stretched
        // across the console: a 16:7 box drawn 700px wide is not what anybody
        // will ever see, and tells you nothing about whether the words fit.
        className={cn(
          'relative mt-2 overflow-hidden rounded-card',
          previewAs === 'phone' ? 'mx-auto w-full max-w-[390px]' : undefined,
        )}
        style={{
          background: form.tint,
          aspectRatio: previewAs === 'wide' ? stripRatio : '16 / 7',
        }}
        data-testid="banner-preview"
      >
        {(previewAs === 'phone' ? (mobilePreview ?? preview) : preview) ? (
          <img
            src={(previewAs === 'phone' ? (mobilePreview ?? preview) : preview) ?? undefined}
            alt=""
            aria-hidden
            // Matches what a buyer gets, including the crop. A preview that
            // always showed the whole image would hide the exact problem this
            // control exists to solve.
            className={cn(
              'absolute inset-0 h-full w-full',
              form.fit === 'contain' ? 'object-contain' : 'object-cover',
            )}
          />
        ) : null}
        {/* The preview shows the darkening too, or somebody turning it off
            cannot see what they have done until it is on the front page. */}
        {form.overlay !== 'none' ? (
          <div
            className="absolute inset-0"
            style={{
              background:
                form.overlay === 'soft'
                  ? 'linear-gradient(90deg, rgba(0,0,0,.34) 0%, rgba(0,0,0,.18) 50%, rgba(0,0,0,0) 100%)'
                  : 'linear-gradient(90deg, rgba(0,0,0,.62) 0%, rgba(0,0,0,.42) 46%, rgba(0,0,0,.08) 100%)',
            }}
          />
        ) : null}
        <div
          className={cn(
            'relative flex h-full flex-col justify-center',
            previewAs === 'wide' ? 'gap-0.5 px-8' : 'gap-1 px-4',
          )}
        >
          <p
            className={cn(
              'm-0 font-display font-bold leading-tight text-white',
              previewAs === 'wide' ? 'max-w-[30ch] text-[21px]' : 'max-w-[22ch] text-[15px]',
            )}
          >
            {form.headline || 'Your headline here'}
          </p>
          {form.subline ? (
            <p
              className={cn(
                'm-0 leading-snug text-white/85',
                previewAs === 'wide' ? 'max-w-[40ch] text-[13px]' : 'max-w-[34ch] text-[11.5px]',
              )}
            >
              {form.subline}
            </p>
          ) : null}
          {form.cta_label ? (
            <span className="mt-1.5 w-fit rounded-btn bg-white px-3 py-1 text-[12px] font-bold text-ink">
              {form.cta_label}
            </span>
          ) : null}
        </div>
      </div>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Input
          label="Headline"
          required
          autoFocus
          maxLength={80}
          value={form.headline}
          hint={`${form.headline.length}/80. Read in about a second, on a phone, by somebody scrolling.`}
          error={errors.headline?.[0]}
          onChange={(event) => set('headline', event.target.value)}
        />

        <Input
          label="The line under it"
          maxLength={140}
          value={form.subline ?? ''}
          hint="Hidden on the narrowest phones, so never put anything essential here."
          error={errors.subline?.[0]}
          onChange={(event) => set('subline', event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Button words"
            maxLength={32}
            value={form.cta_label ?? ''}
            placeholder="Shop now"
            error={errors.cta_label?.[0]}
            onChange={(event) => set('cta_label', event.target.value)}
          />

          <Input
            label="Where it goes"
            value={form.link_path ?? ''}
            placeholder="/search?category=malaria"
            hint="A path inside ChiCare. External addresses are refused."
            error={errors.link_path?.[0]}
            onChange={(event) => set('link_path', event.target.value)}
          />
        </div>

        {/* ── Artwork ──────────────────────────────────────────────────── */}
        <div>
          <p className="m-0 mb-1 text-[11px] font-bold tracking-[0.01em] text-muted">Pictures</p>
          <p className="m-0 mb-2.5 max-w-[60ch] text-[11.5px] leading-relaxed text-faint">
            The strip is a different SHAPE on a phone than on a desktop, so there are two
            canvases. Design at the exact sizes below and nothing is ever cropped or stretched
            — what you draw is what appears, at every screen size. Up to 4MB each.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <ArtworkSlot
              label="Wide"
              // Read from the setting rather than written here, so the size
              // the console asks for and the shape it renders can never drift
              // apart — which is exactly how artwork ends up cropped.
              dimensions={WIDE_CANVAS[stripRatio] ?? WIDE_CANVAS['5/1']!}
              ratio={stripRatio.replace('/', ':')}
              note="640px and up. Keep the left third plain — the headline sits there."
              preview={preview}
              busy={upload.isPending}
              onPick={(file) => upload.mutate({ file, slot: 'wide' })}
              onClear={() => {
                set('image_media_id', null);
                setPreview(null);
              }}
            />

            <ArtworkSlot
              label="Phone"
              dimensions="880 × 385"
              ratio="16:7"
              note="Under 640px. Optional — without it the wide picture is used and cropped."
              preview={mobilePreview}
              busy={upload.isPending}
              onPick={(file) => upload.mutate({ file, slot: 'phone' })}
              onClear={() => {
                set('mobile_image_media_id', null);
                setMobilePreview(null);
              }}
            />
          </div>

          <p className="m-0 mt-2 text-[11px] text-faint">
            A slide with no picture shows the colour below, which is a perfectly good slide.
          </p>
          {upload.error ? (
            <p className="m-0 mt-1.5 text-[11px] font-medium text-danger">
              {upload.error instanceof ApiError ? upload.error.message : 'That upload failed.'}
            </p>
          ) : null}
        </div>

        {/* ── Tint ─────────────────────────────────────────────────────── */}
        <div>
          <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">
            Colour behind it
          </p>
          <div className="flex flex-wrap gap-2">
            {TINTS.map(([value, name]) => (
              <button
                key={value}
                type="button"
                aria-label={name}
                title={name}
                aria-pressed={form.tint === value}
                onClick={() => set('tint', value)}
                className={cn(
                  'h-9 w-9 rounded-btn border-2 transition',
                  form.tint === value ? 'border-ink' : 'border-transparent hover:border-line',
                )}
                style={{ background: value }}
              />
            ))}
          </div>
        </div>

        {/* ── How the picture sits in the strip ─────────────────── */}
        <div>
          <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">
            How the picture sits in the strip
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                ['cover', 'Fill the strip', 'Edge to edge. Crops the top and bottom if the shapes differ'],
                ['contain', 'Show the whole picture', 'Nothing is cropped. Use this for a design with words in it'],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                onClick={() => set('fit', value)}
                aria-pressed={form.fit === value}
                data-testid={`fit-${value}`}
                className={cn(
                  'rounded-card border p-2.5 text-left',
                  form.fit === value ? 'border-blue bg-blue-tint' : 'border-line bg-white',
                )}
              >
                <span
                  className={cn(
                    'block text-[12.5px] font-semibold',
                    form.fit === value ? 'text-blue' : 'text-ink',
                  )}
                >
                  {label}
                </span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-muted">{hint}</span>
              </button>
            ))}
          </div>
          <p className="m-0 mt-1.5 text-[11px] leading-snug text-faint">
            If your design is being cut off, the real fix is to match the strip's SHAPE to it — set
            that once in the panel above the slide list, and nothing is cropped or letterboxed.
          </p>
        </div>

        {/* ── Darkening ────────────────────────────────── */}
        <div>
          <p className="m-0 mb-[5px] text-[11px] font-bold tracking-[0.01em] text-muted">
            Darkening over the picture
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {(
              [
                ['none', 'None', 'Your picture exactly as uploaded. The default.'],
                ['soft', 'Light', 'A little, for a bright photograph'],
                ['auto', 'Strong', 'A lot, when the picture is very light'],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                onClick={() => set('overlay', value)}
                aria-pressed={form.overlay === value}
                data-testid={`overlay-${value}`}
                className={cn(
                  'rounded-card border p-2.5 text-left',
                  form.overlay === value ? 'border-blue bg-blue-tint' : 'border-line bg-white',
                )}
              >
                <span
                  className={cn(
                    'block text-[12.5px] font-semibold',
                    form.overlay === value ? 'text-blue' : 'text-ink',
                  )}
                >
                  {label}
                </span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-muted">{hint}</span>
              </button>
            ))}
          </div>
          <p className="m-0 mt-1.5 text-[11px] leading-snug text-faint">
            The words carry their own shadow, so they stay readable over almost any picture
            without dimming it. Only reach for these if your photograph is light enough that the
            headline still gets lost in it.
          </p>
        </div>

        {/* ── Where and when ───────────────────────────────────────────── */}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-[5px] block text-[11px] font-bold text-muted">Which shelf</span>
            <select
              value={form.sector}
              onChange={(event) => set('sector', event.target.value as BannerInput['sector'])}
              className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink"
            >
              <option value="both">Both shelves</option>
              <option value="human">Human medicines only</option>
              <option value="veterinary">Animal health only</option>
            </select>
            <span className="mt-1.5 block text-[11px] text-faint">
              The two shops are walled. A slide about a children's syrup must not appear above the
              cattle shelf.
            </span>
          </label>

          <label className="block">
            <span className="mb-[5px] block text-[11px] font-bold text-muted">Row</span>
            <select
              value={form.row}
              onChange={(event) => set('row', Number(event.target.value))}
              className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink"
            >
              <option value={1}>Row 1 — the top strip</option>
              <option value={2}>Row 2</option>
              <option value={3}>Row 3</option>
            </select>
            <span className="mt-1.5 block text-[11px] text-faint">
              Rows beyond the number set in Settings are not sent to buyers at all.
            </span>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Start showing"
            type="date"
            value={form.starts_at ?? ''}
            hint="Leave empty to start now."
            error={errors.starts_at?.[0]}
            onChange={(event) => set('starts_at', event.target.value)}
          />
          <Input
            label="Stop showing"
            type="date"
            value={form.ends_at ?? ''}
            hint="Leave empty to run until you switch it off."
            error={errors.ends_at?.[0]}
            onChange={(event) => set('ends_at', event.target.value)}
          />
        </div>

        <label className="flex items-start gap-2.5 rounded-card border border-line bg-white p-3">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span className="text-[12.5px] leading-relaxed text-ink">
            Switched on
            <span className="mt-0.5 block text-[11px] text-muted">
              Off keeps the slide and its figures but takes it off the shop front immediately.
            </span>
          </span>
        </label>

        {save.error instanceof ApiError && !save.error.isValidation ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-danger/25 bg-danger-tint px-3 py-2.5 text-[12px] text-danger"
          >
            <Icon name="alert" size={14} className="mt-px shrink-0" />
            <span>{save.error.message}</span>
          </div>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" loading={save.isPending} disabled={!form.headline.trim()}>
            {banner ? 'Save the slide' : 'Publish the slide'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ── Categories ──────────────────────────────────────────────────────

/**
 * One canvas, with the size it has to be drawn at stated on it.
 *
 * The dimensions are the whole reason this component exists. An admin given
 * a bare "choose a picture" button uploads whatever their designer sent, it
 * is cropped to a shape nobody mentioned, and the words baked into the
 * artwork disappear off the side. Saying 1600 × 320 BEFORE they pick is what
 * prevents that.
 */
function ArtworkSlot({
  label,
  dimensions,
  ratio,
  note,
  preview,
  busy,
  onPick,
  onClear,
}: {
  label: string;
  dimensions: string;
  ratio: string;
  note: string;
  preview: string | null;
  busy: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="rounded-card border border-line bg-white p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-bold text-ink">{label}</span>
        <span className="font-mono text-[10px] tracking-[0.04em] text-blue">
          {dimensions} &middot; {ratio}
        </span>
      </div>

      <div
        // Shown at the ratio it will actually be used at, so a picture of
        // the wrong shape is obvious here rather than on the front page.
        className={cn(
          'relative mt-2 overflow-hidden rounded bg-paper',
        )}
        style={{ aspectRatio: ratio.replace(':', ' / ') }}
      >
        {preview ? (
          <img src={preview} alt="" aria-hidden className="h-full w-full object-cover" />
        ) : (
          <span className="grid h-full w-full place-items-center text-[10.5px] text-faint">
            Nothing yet
          </span>
        )}
      </div>

      <p className="m-0 mt-1.5 text-[10.5px] leading-snug text-faint">{note}</p>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onPick(file);
          }
          // Cleared so picking the SAME file again still fires a change.
          event.target.value = '';
        }}
      />

      <div className="mt-1.5 flex gap-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="min-h-9 flex-1 rounded-btn border border-line bg-white text-[11px] font-semibold text-muted hover:border-blue hover:text-blue disabled:opacity-40"
        >
          {preview ? 'Replace' : 'Choose a picture'}
        </button>

        {preview ? (
          <button
            type="button"
            aria-label={`Remove the ${label.toLowerCase()} picture`}
            onClick={onClear}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-btn border border-line bg-white text-danger hover:border-danger"
          >
            <Icon name="trash" size={13} strokeWidth={2} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CategoryPictures() {
  const queryClient = useQueryClient();
  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'shopfront', 'categories'],
    queryFn: ({ signal }) => api.shopFrontCategories(signal),
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="aspect-square w-full" radius="card" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <AdminError title="We couldn't load the categories" error={error} onRetry={() => refetch()} />
    );
  }

  const categories = data ?? [];
  const shelves = [
    ['human', 'Human medicines'],
    ['veterinary', 'Animal health'],
  ] as const;

  return (
    <div>
      <p className="m-0 mb-4 max-w-[64ch] text-[12.5px] leading-relaxed text-muted">
        A category with no picture keeps the drawn pack art, so a half-photographed shelf looks
        deliberate rather than broken — and so does one whose picture fails to load, which is
        what a missing <code className="font-mono text-[11px]">storage:link</code> on the server
        looks like. How many tiles a buyer sees is set in{' '}
        <b className="font-semibold text-ink">Settings &rsaquo; Shop front</b>; the first ones in
        each shelf's order are the ones that show.
      </p>

      <p className="m-0 mb-4 max-w-[64ch] text-[12.5px] leading-relaxed text-muted">
        A tile does not have to lead to its own shelf. Point one at the prescription board, the
        pack verifier, an article or a page, and buyers who press it go there instead — useful for
        a campaign, and the only way to put anything but a drug category in the first six squares
        a buyer sees.
      </p>

      <RailTiming />

      <AddCategory
        onAdded={() =>
          queryClient.invalidateQueries({ queryKey: ['admin', 'shopfront', 'categories'] })
        }
      />

      {shelves.map(([sector, label]) => {
        const inShelf = categories.filter((category) => category.sector === sector);

        if (inShelf.length === 0) {
          return null;
        }

        return (
          <section key={sector} className="mb-6">
            <h2 className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {label}
            </h2>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {inShelf.map((category) => (
                <TilePicture
                  key={category.id}
                  category={category}
                  onChanged={() =>
                    queryClient.invalidateQueries({ queryKey: ['admin', 'shopfront', 'categories'] })
                  }
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * How fast the category rail drifts.
 *
 * ────────────────── WHY IT IS HERE AND NOT ONLY IN SETTINGS ──────────────────
 *
 * Same reason as the carousel's timing: the person arranging categories is the
 * person who wants to know how fast they move, and making them leave for a
 * settings group two screens away is how a control goes unfound.
 *
 * Same setting, same endpoint, same audit trail.
 */
function RailTiming() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: ({ signal }) => api.adminSettings(signal),
  });

  const values = (data?.values ?? {}) as Record<string, unknown>;

  const seconds = Number(values['shopfront.category_scroll_seconds'] ?? 5);
  const drifts = values['shopfront.category_scroll'] !== false;

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateAdminSettings(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  return (
    <div className="mb-4 rounded-card border border-line bg-paper p-3.5" data-testid="rail-timing">
      <p className="m-0 text-[12px] font-semibold text-ink">How the row of tiles behaves</p>
      <p className="m-0 mt-0.5 text-[11px] leading-snug text-muted">
        The tiles are one row that scrolls sideways, at every screen size. Twenty categories fit as
        happily as six.
      </p>

      <div className="mt-2.5 flex flex-wrap items-end gap-x-6 gap-y-3">
        <label className="block">
          <span className="mb-[5px] block text-[11px] font-bold text-muted">
            Seconds to drift past one tile
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Slower"
              disabled={save.isPending || seconds >= 30 || !drifts}
              onClick={() =>
                save.mutate({ 'shopfront.category_scroll_seconds': Math.min(30, seconds + 1) })
              }
              className="grid h-10 w-10 shrink-0 place-items-center rounded-btn border border-line bg-white disabled:opacity-40"
            >
              <Icon name="minus" size={14} strokeWidth={2.4} />
            </button>

            <span
              className="min-w-[46px] text-center font-mono text-[15px] font-bold text-ink"
              data-testid="rail-seconds"
            >
              {seconds}s
            </span>

            <button
              type="button"
              aria-label="Faster"
              disabled={save.isPending || seconds <= 2 || !drifts}
              onClick={() =>
                save.mutate({ 'shopfront.category_scroll_seconds': Math.max(2, seconds - 1) })
              }
              className="grid h-10 w-10 shrink-0 place-items-center rounded-btn border border-line bg-white disabled:opacity-40"
            >
              <Icon name="plus" size={14} strokeWidth={2.4} />
            </button>
          </div>
          {/* Higher is SLOWER, which is the opposite of what a speed control
              usually means, so the buttons are labelled by what they do to the
              movement rather than to the number. */}
          <span className="mt-1 block text-[10.5px] leading-snug text-faint">
            Higher is slower. Below about three seconds it reads as a machine scrolling.
          </span>
        </label>

        <label className="flex items-start gap-2 pb-5">
          <input
            type="checkbox"
            checked={drifts}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ 'shopfront.category_scroll': event.target.checked })
            }
            className="mt-0.5 h-4 w-4"
          />
          <span className="text-[12px] leading-snug text-ink">
            Drift by itself
            <span className="mt-0.5 block text-[10.5px] text-faint">
              Off still scrolls — people swipe it.
            </span>
          </span>
        </label>
      </div>

      <p className="m-0 mt-2 text-[10.5px] leading-snug text-faint">
        It stops the moment anybody hovers, touches or tabs into it, and never moves at all for
        somebody whose device asks for reduced motion.
      </p>

      {save.error ? (
        <p className="m-0 mt-1.5 text-[11px] font-medium text-danger">
          {save.error instanceof ApiError ? save.error.message : 'That did not save.'}
        </p>
      ) : null}
    </div>
  );
}

// Sentinels for the two rows that are not themselves destinations. Prefixed
// with @ because a real path always begins with /, so they can never collide
// with one.
const SEARCH_MODE = '@search';

const CUSTOM_MODE = '@custom';

const SEARCH_PREFIX = '/search?q=';

/**
 * Where a tile leads.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY THIS EXISTS AT ALL
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every tile on the shop front pointed at its own shelf, which was never a
 * decision — it was the only thing a tile could mean when a drug catalogue was
 * the only thing on ChiCare. There is now a prescription board, a pack
 * verifier, a blog, a set of CMS pages and a seller registration. None of them
 * were reachable from the six squares a buyer sees first.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY A LIST AND NOT A TEXT BOX
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A text box asking for a path is a text box somebody fills in from memory,
 * and `/Verify`, `/prescription` and `/pages/returns-polcy` are all 404s that
 * look exactly like working links from in here. The options come from the
 * server, which knows which pages and articles are actually PUBLISHED — a tile
 * pointed at a draft works for the person who wrote it and for nobody else,
 * which is the worst possible way to find out.
 *
 * The typed path is still offered, last, for the destination nobody predicted.
 * It is checked as a ChiCare path and NOT as a screen that exists, and the
 * hint under it says so rather than implying a check that is not happening.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY THE KIND IS DERIVED AND NOT STORED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * One column holds the finished path. The alternative — a kind plus a value,
 * resolved at render time — gives two facts that can disagree, and when they
 * disagree a buyer goes somewhere nobody chose. So the path is the truth and
 * this screen reads the kind back out of it.
 */
function DestinationPicker({
  value,
  onChange,
  busy,
  testId,
}: {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  busy?: boolean;
  testId?: string;
}) {
  const { data } = useQuery({
    queryKey: ['admin', 'shopfront', 'destinations'],
    queryFn: ({ signal }) => api.shopFrontDestinations(signal),
    // The pages and articles do not change while somebody arranges tiles, and
    // this is mounted once per tile — without this it is a dozen identical
    // requests for one screen.
    staleTime: 5 * 60_000,
  });

  const groups: ShopFrontDestinationGroup[] = data ?? [];
  const known = groups.flatMap((group) => group.options);

  const path = value || '';
  const searchTerm = path.startsWith(SEARCH_PREFIX)
    ? decodeURIComponent(path.slice(SEARCH_PREFIX.length))
    : '';

  /**
   * Which row of the list the STORED path is.
   *
   * ── THE BUG THIS SHAPE EXISTS TO PREVENT ──────────────────────────────
   *
   * This used to fall back to "A path I type myself…" whenever the path was
   * not in `known`, which looks right and is wrong for one reason: on the
   * first render `known` is EMPTY, because the options are still arriving.
   * So every tile with a destination opened in the typing mode, showing a
   * text box over a path that had been chosen from the list — and the
   * browser test caught it reading `@custom` where `/verify` was stored.
   *
   * A path that is not in the list is now simply ITSELF, with an option
   * rendered for it below. That is correct while the list loads, correct for
   * a hand-typed path, and has no loading state to get wrong.
   */
  const stored = !path ? '' : searchTerm && !known.some((o) => o.path === path) ? SEARCH_MODE : path;

  // The user's own choice this session, which overrides the stored one —
  // including the two rows that are not destinations at all.
  const [chosen, setChosen] = useState<string | null>(null);
  const [typed, setTyped] = useState<string | null>(null);

  const mode = chosen ?? stored;
  const typing = mode === SEARCH_MODE || mode === CUSTOM_MODE;

  // `??` and not `||`: an empty box the user has deliberately cleared must
  // stay empty rather than snapping back to the stored path.
  const draft = typed ?? (mode === SEARCH_MODE ? searchTerm : path);

  const choose = (next: string) => {
    setChosen(next);
    setTyped(null);

    // Nothing is saved for the typing rows yet. A half-typed term is not a
    // destination, and saving per keystroke would store `/search?q=d` on the
    // way to `/search?q=dewormer`.
    if (next === SEARCH_MODE || next === CUSTOM_MODE) {
      return;
    }

    onChange(next || null);
  };

  const commit = () => {
    const trimmed = draft.trim();

    if (!trimmed) {
      return;
    }

    onChange(
      mode === SEARCH_MODE
        ? `${SEARCH_PREFIX}${encodeURIComponent(trimmed)}`
        : trimmed.startsWith('/')
          ? trimmed
          : `/${trimmed}`,
    );
  };

  return (
    <div className="mt-1.5">
      <label className="block">
        <span className="mb-[3px] block text-[10px] font-bold uppercase tracking-[0.08em] text-faint">
          Points to
        </span>
        <select
          value={mode}
          disabled={busy}
          data-testid={testId}
          onChange={(event) => choose(event.target.value)}
          className="min-h-9 w-full rounded-btn border border-line bg-white px-2 text-[11.5px] text-ink disabled:opacity-50"
        >
          <option value="">Its own shelf</option>
          <option value={SEARCH_MODE}>A search for a word…</option>

          {/* The stored path, when the list does not contain it — either
              because it was typed by hand, or because the options have not
              arrived yet. Without this the <select> has a value with no
              matching option, which browsers resolve by showing the FIRST
              option: a tile pointing at /verify would read "Its own shelf". */}
          {path && !known.some((option) => option.path === path) && mode === path ? (
            <option value={path}>{path}</option>
          ) : null}

          {groups
            // A shop with no articles yet should not show an empty "Articles"
            // heading, which reads as a list that failed to load.
            .filter((group) => group.options.length > 0)
            .map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map((option) => (
                  <option key={option.path} value={option.path}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}

          <option value={CUSTOM_MODE}>A path I type myself…</option>
        </select>
      </label>

      {typing ? (
        <div className="mt-1 flex gap-1">
          <input
            autoFocus
            value={draft}
            maxLength={200}
            placeholder={mode === SEARCH_MODE ? 'dewormer' : '/pages/returns-policy'}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commit();
              }
            }}
            data-testid={testId ? `${testId}-value` : undefined}
            className="min-h-9 w-full min-w-0 rounded-btn border border-line bg-white px-2 text-[11.5px] text-ink"
          />
          <button
            type="button"
            onClick={commit}
            disabled={!draft.trim() || busy}
            data-testid={testId ? `${testId}-set` : undefined}
            className="min-h-9 shrink-0 rounded-btn bg-blue px-2 text-[11px] font-semibold text-white disabled:opacity-40"
          >
            Set
          </button>
        </div>
      ) : null}

      {/* The path itself, under the name. Two articles can have near-identical
          titles, and this is the line that says which one was chosen. */}
      {path && !typing ? (
        <p className="m-0 mt-1 truncate font-mono text-[10px] text-faint" title={path}>
          {path}
        </p>
      ) : null}

      {mode === CUSTOM_MODE ? (
        <p className="m-0 mt-1 text-[10px] leading-snug text-caution">
          A path inside ChiCare, starting with /. It is not checked against the app&rsquo;s
          screens, so press the tile once on the shop front to be sure.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Adding a category to a shelf.
 *
 * — WHY THE SHELF IS CHOSEN HERE AND NEVER AGAIN —
 *
 * Because `Product` derives its sector FROM its category. Filing a category on
 * the wrong side of the wall puts every product under it on the wrong shelf —
 * cattle antibiotics among the children's medicines — and moving it later
 * drags all of them across with it, which is a catalogue migration rather than
 * an edit. So it is a deliberate choice at the one moment it is cheap, and
 * absent from every form afterwards.
 */
function AddCategory({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [sector, setSector] = useState<'human' | 'veterinary'>('human');
  const [linkPath, setLinkPath] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createCategory({ name: name.trim(), sector, link_path: linkPath }),
    onSuccess: () => {
      setName('');
      setLinkPath(null);
      setOpen(false);
      onAdded();
    },
  });

  const errors = create.error instanceof ApiError ? create.error.fieldErrors : {};

  if (!open) {
    return (
      <Button variant="grey" onClick={() => setOpen(true)} data-testid="category-new">
        Add a category
      </Button>
    );
  }

  return (
    <form
      className="mb-5 rounded-card border border-line bg-white p-3.5"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <p className="m-0 mb-2.5 text-[13px] font-semibold text-ink">A new category</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Name"
          autoFocus
          required
          maxLength={60}
          value={name}
          placeholder="Vitamins &amp; supplements"
          error={errors.name?.[0]}
          onChange={(event) => setName(event.target.value)}
        />

        <label className="block">
          <span className="mb-[5px] block text-[11px] font-bold text-muted">Which shelf</span>
          <select
            value={sector}
            onChange={(event) => setSector(event.target.value as 'human' | 'veterinary')}
            data-testid="category-sector"
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink"
          >
            <option value="human">Human medicines</option>
            <option value="veterinary">Animal health</option>
          </select>
          <span className="mt-1.5 block text-[11px] leading-snug text-faint">
            This cannot be changed later. Everything filed under it inherits this shelf.
          </span>
        </label>
      </div>

      {/* ── A TILE NEED NOT BE A SHELF ───────────────────────────────────
          Offered at creation and not only afterwards, because the commonest
          reason to add a category here is to put a tile on the shop front
          pointing at something — a campaign, the prescription board, an
          article. Having to save it and then edit it to say where it goes is
          two steps for one intention.

          Unlike the shelf above, this IS changeable later: it is a link, not
          a catalogue decision, and nothing inherits it. */}
      <div className="mt-3 max-w-[340px]">
        <DestinationPicker
          value={linkPath}
          onChange={setLinkPath}
          testId="new-category-destination"
        />
      </div>

      {errors.link_path ? (
        <p className="m-0 mt-2 text-[11px] font-medium text-danger">{errors.link_path[0]}</p>
      ) : null}

      {errors.sector ? (
        <p className="m-0 mt-2 text-[11px] font-medium text-danger">{errors.sector[0]}</p>
      ) : null}

      <p className="m-0 mt-2.5 text-[11px] leading-relaxed text-faint">
        It is added switched off, so buyers never see an empty shelf. Give it a picture and some
        products, then press <b className="font-semibold text-ink">Hidden — show it</b>.
      </p>

      <div className="mt-3 flex gap-2">
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          Add it
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * One click out of "switched on and invisible".
 *
 * ── WHY A BUTTON AND NOT JUST THE SENTENCE ─────────────────────────────────
 *
 * The server's message says "Move it up, or raise the count in Settings", and
 * both halves of that were advice somebody could not take from this screen.
 * There is no reorder control for categories here at all, and Settings is two
 * screens away under a heading that does not mention categories — so the
 * honest reading of that sentence was "your tile is invisible and the fix is
 * elsewhere, good luck".
 *
 * The count is a plain number setting and this screen already writes another
 * one (see RailTiming, which sets the drift speed from right here, for exactly
 * the same reason: the person arranging tiles is the person who wants to
 * change how many show).
 *
 * ── WHY IT RAISES TO EXACTLY WHAT IS NEEDED ────────────────────────────────
 *
 * Not +1, which leaves the next one hidden and needs pressing again. Not a big
 * round number, which quietly puts three half-finished categories on the shop
 * front as a side effect. It counts how many are switched on for this shelf
 * and asks for that — so every tile the admin has deliberately turned on
 * becomes visible, and nothing else does.
 *
 * It says the number before it is pressed, because a control that changes what
 * buyers see should never be a surprise.
 */
function RaiseTileCount({ category }: { category: CategoryArtRow }) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['admin', 'shopfront', 'categories'],
    queryFn: ({ signal }) => api.shopFrontCategories(signal),
  });

  // How many this shelf needs for everything switched on to be visible. The
  // OTHER shelf is irrelevant: the count is one setting shared by both, so
  // this takes whichever shelf needs more and never lowers it.
  const needed = Math.max(
    ...(['human', 'veterinary'] as const).map(
      (sector) =>
        (data ?? []).filter((one) => one.sector === sector && one.is_active !== false).length,
    ),
    1,
  );

  const save = useMutation({
    mutationFn: () => api.updateAdminSettings({ 'shopfront.category_count': needed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'shopfront', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  // Only offered for the reason it actually fixes. A category that is hidden
  // because it is switched OFF is not helped by more tiles, and a button that
  // does nothing for your problem is worse than no button.
  if (!category.hidden_because?.includes('tiles')) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        data-testid={`raise-tiles-${category.slug}`}
        className="mt-1 min-h-8 rounded-btn bg-caution px-2 text-[10.5px] font-bold text-ink disabled:opacity-50"
      >
        {save.isPending ? 'Showing it…' : `Show ${needed} tiles on each shelf`}
      </button>

      {save.error ? (
        <p className="m-0 mt-1 text-[10px] font-medium leading-snug text-danger">
          {save.error instanceof ApiError ? save.error.message : 'That did not save.'}
        </p>
      ) : null}
    </>
  );
}

function TilePicture({
  category,
  onChanged,
}: {
  category: CategoryArtRow;
  onChanged: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const live = useMutation({
    mutationFn: (isActive: boolean) => api.updateCategory(category.id, { is_active: isActive }),
    onSuccess: onChanged,
  });

  // Its own mutation and not folded into `live`, so a failed link does not
  // read as a failed switch — and so the tile does not flicker between
  // Showing and Hidden while a path saves.
  const destination = useMutation({
    mutationFn: (linkPath: string | null) =>
      api.updateCategory(category.id, { link_path: linkPath }),
    onSuccess: onChanged,
  });

  const save = useMutation({
    mutationFn: async (file: File | null) => {
      if (!file) {
        return api.setCategoryArt(category.id, null);
      }

      const asset = await api.uploadMedia(file, 'categories', category.name);

      return api.setCategoryArt(category.id, asset.id);
    },
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteCategory(category.id),
    onSuccess: onChanged,
  });

  return (
    <div data-testid={`tile-${category.slug}`}>
      <div className="relative aspect-square overflow-hidden rounded-card border border-line bg-paper">
        {category.image ? (
          <img
            src={category.image}
            alt=""
            aria-hidden
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="grid h-full w-full place-items-center text-[10.5px] font-medium text-faint">
            Drawn art
          </span>
        )}

        {save.isPending ? (
          <span className="absolute inset-0 grid place-items-center bg-white/70 text-[11px] font-semibold text-blue">
            Saving…
          </span>
        ) : null}
      </div>

      <p className="m-0 mt-1.5 truncate text-[12px] font-semibold text-ink">{category.name}</p>

      {/* ════════════════════════════════════════════════════════════════
          WHETHER BUYERS CAN SEE IT — WHICH IS NOT THE SAME AS "SWITCHED ON"

          This said "✓ Showing" the moment `is_active` was true, and `is_active`
          is only half the question. A tile beyond the shelf's tile count is
          switched on, saved, correct in every field — and absent from the shop
          front. The console said Showing; the home screen showed nothing.

          Reported exactly that way: a new category, switched on, pointed at
          the blog, invisible to buyers, with the console insisting it was
          there. The reason WAS being returned by the server all along, and
          was rendered at the very bottom of this card, below the red Delete
          button, where nobody looks after pressing a control that just told
          them it worked.

          So the state has three readings now, not two, and the one that means
          "you are not finished" is the loudest.
          ════════════════════════════════════════════════════════════════ */}
      {category.is_active === false ? (
        <button
          type="button"
          onClick={() => live.mutate(true)}
          disabled={live.isPending}
          className="mt-0.5 flex min-h-8 items-center gap-1 text-[11px] font-semibold text-blue disabled:opacity-50"
        >
          <Icon name="eye" size={12} />
          Hidden — show it
        </button>
      ) : category.on_shop_front === false ? (
        <button
          type="button"
          onClick={() => live.mutate(false)}
          disabled={live.isPending}
          className="mt-0.5 flex min-h-8 items-center gap-1 text-[11px] font-semibold text-caution disabled:opacity-50"
        >
          <Icon name="alert" size={12} strokeWidth={2.4} />
          On, but buyers cannot see it
        </button>
      ) : (
        <button
          type="button"
          onClick={() => live.mutate(false)}
          disabled={live.isPending}
          className="mt-0.5 flex min-h-8 items-center gap-1 text-[11px] text-faint hover:text-ink disabled:opacity-50"
        >
          <Icon name="check" size={12} strokeWidth={2.6} className="text-verified" />
          Showing
        </button>
      )}

      {/* The reason, immediately under the claim it contradicts — and no
          longer at the foot of the card under the delete button. */}
      {category.hidden_because ? (
        <div
          data-testid={`why-hidden-${category.slug}`}
          className="mt-1 rounded-[7px] border border-caution/30 bg-caution-tint px-2 py-1.5"
        >
          <p className="m-0 text-[10.5px] font-medium leading-snug text-caution-ink">
            {category.hidden_because}
          </p>

          <RaiseTileCount category={category} />
        </div>
      ) : null}

      <DestinationPicker
        value={category.link_path}
        onChange={(next) => destination.mutate(next)}
        busy={destination.isPending}
        testId={`destination-${category.slug}`}
      />

      {destination.error ? (
        <p className="m-0 mt-1 text-[10.5px] font-medium leading-snug text-danger">
          {destination.error instanceof ApiError
            ? destination.error.message
            : 'That link did not save.'}
        </p>
      ) : null}

      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            save.mutate(file);
          }
        }}
      />

      {/* ────────────────── TWO DIFFERENT DESTRUCTIVE THINGS ──────────────────

          The trash icon used to clear the PICTURE, which is not what a trash
          icon means next to a category. Somebody pressing it expected the
          category gone and got an empty tile still sitting there.

          So they are separate and named: "Remove picture" says what it does,
          and Delete removes the category itself. */}
      <div className="mt-1 flex gap-1">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="min-h-9 flex-1 rounded-btn border border-line bg-white text-[11px] font-semibold text-muted hover:border-blue hover:text-blue"
        >
          {category.image ? 'Replace' : 'Add a picture'}
        </button>

        {category.image ? (
          <button
            type="button"
            onClick={() => save.mutate(null)}
            title={`Remove the picture from ${category.name}`}
            className="min-h-9 shrink-0 rounded-btn border border-line bg-white px-2 text-[11px] font-semibold text-muted hover:border-blue hover:text-blue"
          >
            Remove picture
          </button>
        ) : null}
      </div>

      <button
        type="button"
        data-testid={`delete-category-${category.slug}`}
        disabled={remove.isPending}
        onClick={() => {
          // Confirmed by name, not by a bare "are you sure". A grid of tiles
          // is exactly where somebody deletes the one next to the one they
          // meant, and the name is what catches that.
          if (
            window.confirm(
              `Delete the category “${category.name}”?\n\n` +
                'This cannot be undone. The picture stays in the media library.\n\n' +
                'If you only want it off the shop front, press Showing instead — that is reversible.',
            )
          ) {
            remove.mutate();
          }
        }}
        className="mt-1 flex min-h-8 w-full items-center justify-center gap-1 rounded-btn text-[11px] font-semibold text-danger hover:bg-danger-tint disabled:opacity-50"
      >
        <Icon name="trash" size={12} strokeWidth={2} />
        Delete category
      </button>

      {/* The refusal, in full. "7 products are filed under this" is something
          somebody can act on; a red cross is not. */}
      {remove.error ? (
        <p className="m-0 mt-1 text-[10.5px] leading-snug font-medium text-danger">
          {remove.error instanceof ApiError
            ? remove.error.message
            : 'That category could not be deleted.'}
        </p>
      ) : null}

      {save.error ? (
        <p className="m-0 mt-1 text-[10.5px] font-medium text-danger">
          {save.error instanceof ApiError ? save.error.message : 'That did not save.'}
        </p>
      ) : null}
    </div>
  );
}
